import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { label: "", amount: "", date: "", notes: "" };

export default function CashAdvances() {
    const [advances, setAdvances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        fetchAdvances();
    }, []);

    const fetchAdvances = async () => {
        try {
            const res = await axios.get(`${API}/cash-advances`);
            setAdvances(res.data);
        } catch {
            toast.error("Impossible de charger les avances");
        } finally {
            setLoading(false);
        }
    };

    const openNew = () => {
        setEditing(null);
        setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
        setDialogOpen(true);
    };

    const openEdit = (adv) => {
        setEditing(adv);
        setForm({ label: adv.label, amount: String(adv.amount), date: adv.date, notes: adv.notes || "" });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = { ...form, amount: parseFloat(form.amount) };
        try {
            if (editing) {
                await axios.put(`${API}/cash-advances/${editing.id}`, payload);
                toast.success("Avance mise à jour");
            } else {
                await axios.post(`${API}/cash-advances`, payload);
                toast.success("Avance ajoutée");
            }
            setDialogOpen(false);
            fetchAdvances();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/cash-advances/${deleteTarget.id}`);
            toast.success("Avance supprimée");
            setDeleteTarget(null);
            fetchAdvances();
        } catch {
            toast.error("Erreur lors de la suppression");
        }
    };

    const total = advances.reduce((sum, a) => sum + a.amount, 0);

    const formatAmount = (v) =>
        new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);

    const formatDate = (d) =>
        new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Avances de Trésorerie</h1>
                    <p className="page-subtitle">Gérez vos avances et consultez le disponible total</p>
                </div>
                <Button className="rounded-none text-xs uppercase tracking-wider gap-2" onClick={openNew}>
                    <Plus className="w-4 h-4" />
                    Nouvelle avance
                </Button>
            </div>

            <div className="page-content">
                {/* Total card */}
                <div className="border border-zinc-200 bg-white p-6 flex items-center justify-between mb-6">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total disponible</p>
                        <p className="text-4xl font-semibold font-mono mt-1 text-green-600">{formatAmount(total)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{advances.length} avance{advances.length !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="w-16 h-16 bg-green-50 flex items-center justify-center">
                        <Wallet className="w-8 h-8 text-green-600" />
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="text-center text-muted-foreground py-12">Chargement…</div>
                ) : advances.length === 0 ? (
                    <div className="border border-dashed border-zinc-200 p-12 text-center text-muted-foreground text-sm">
                        Aucune avance enregistrée. Cliquez sur <strong>Nouvelle avance</strong> pour commencer.
                    </div>
                ) : (
                    <div className="border border-zinc-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-zinc-100 bg-zinc-50">
                                    <th className="text-left p-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Date</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Libellé</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Notes</th>
                                    <th className="text-right p-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Montant</th>
                                    <th className="p-3 w-20" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {advances.map((adv) => (
                                    <tr key={adv.id} className="hover:bg-zinc-50/50 transition-colors">
                                        <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{formatDate(adv.date)}</td>
                                        <td className="p-3 font-medium">{adv.label}</td>
                                        <td className="p-3 text-muted-foreground text-xs max-w-xs truncate">{adv.notes || "—"}</td>
                                        <td className="p-3 text-right font-mono font-semibold text-green-600">{formatAmount(adv.amount)}</td>
                                        <td className="p-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none" onClick={() => openEdit(adv)}>
                                                    <Pencil className="w-3 h-3" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(adv)}>
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-zinc-200 bg-zinc-50">
                                    <td colSpan={3} className="p-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Total</td>
                                    <td className="p-3 text-right font-mono font-bold text-green-600">{formatAmount(total)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>

            {/* Create / Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md rounded-none">
                    <DialogHeader>
                        <DialogTitle className="uppercase text-xs tracking-wider font-mono">
                            {editing ? "Modifier l'avance" : "Nouvelle avance de trésorerie"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-1">
                            <Label className="text-xs uppercase tracking-wider">Libellé *</Label>
                            <Input
                                className="rounded-none"
                                placeholder="ex : Virement client Mars"
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs uppercase tracking-wider">Montant (€) *</Label>
                                <Input
                                    className="rounded-none"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs uppercase tracking-wider">Date *</Label>
                                <Input
                                    className="rounded-none"
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs uppercase tracking-wider">Notes</Label>
                            <Textarea
                                className="rounded-none resize-none"
                                rows={2}
                                placeholder="Remarques optionnelles…"
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            />
                        </div>
                        <DialogFooter className="gap-2">
                            <Button type="button" variant="outline" className="rounded-none text-xs uppercase tracking-wider" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="rounded-none text-xs uppercase tracking-wider">
                                {editing ? "Enregistrer" : "Ajouter"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cette avance ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong>{deleteTarget?.label}</strong> ({deleteTarget ? formatAmount(deleteTarget.amount) : ""}) sera supprimée définitivement.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Annuler</AlertDialogCancel>
                        <AlertDialogAction className="rounded-none bg-red-600 hover:bg-red-700" onClick={handleDelete}>
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
